import React from 'react';
import { Box, Paper, Typography, Alert, Divider } from '@mui/material';
import { useAuth } from '../context/AuthContext';

const AuthDebug = () => {
  const { user, accessToken, isAuthenticated } = useAuth();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        🔍 Authentication Debug
      </Typography>

      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Authentication Status
        </Typography>

        <Alert severity={isAuthenticated ? 'success' : 'error'} sx={{ mb: 2 }}>
          {isAuthenticated ? '✅ Authenticated' : '❌ Not Authenticated'}
        </Alert>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" gutterBottom>
          <strong>User Info:</strong>
        </Typography>
        {user ? (
          <Box sx={{ ml: 2, mb: 2 }}>
            <Typography>• Email: {user.email}</Typography>
            <Typography>• First Name: {user.firstName}</Typography>
            <Typography>• Last Name: {user.lastName}</Typography>
            <Typography>• Phone: {user.phone}</Typography>
            <Typography>• Role: <strong>{user.role}</strong></Typography>
            <Typography>• User ID: {user._id}</Typography>
          </Box>
        ) : (
          <Typography color="error" sx={{ ml: 2, mb: 2 }}>
            No user data available
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" gutterBottom>
          <strong>Access Token:</strong>
        </Typography>
        {accessToken ? (
          <Box sx={{ ml: 2, mb: 2 }}>
            <Typography sx={{ wordBreak: 'break-all', fontSize: '0.875rem', color: 'success.main' }}>
              {accessToken.substring(0, 50)}...
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Token exists and is {accessToken.length} characters long
            </Typography>
          </Box>
        ) : (
          <Typography color="error" sx={{ ml: 2, mb: 2 }}>
            ❌ No access token found
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" gutterBottom>
          <strong>LocalStorage Check:</strong>
        </Typography>
        <Box sx={{ ml: 2, mb: 2 }}>
          <Typography>
            • accessToken: {localStorage.getItem('accessToken') ? '✅ Present' : '❌ Missing'}
          </Typography>
          <Typography>
            • refreshToken: {localStorage.getItem('refreshToken') ? '✅ Present' : '❌ Missing'}
          </Typography>
          <Typography>
            • user: {localStorage.getItem('user') ? '✅ Present' : '❌ Missing'}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" gutterBottom>
          <strong>Permissions:</strong>
        </Typography>
        <Box sx={{ ml: 2, mb: 2 }}>
          {user ? (
            <>
              <Typography>
                • Can access Pathasala Management: {['Admin', 'SuperUser'].includes(user.role) ? '✅ Yes' : '❌ No'}
              </Typography>
              <Typography>
                • Can access Vatu Registration: {['Admin', 'SuperUser', 'PathashalaAdmin'].includes(user.role) ? '✅ Yes' : '❌ No'}
              </Typography>
            </>
          ) : (
            <Typography color="error">User must login first</Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          {!isAuthenticated && (
            <Alert severity="info" sx={{ flex: 1 }}>
              Please click the login icon in the top-right corner to log in.
            </Alert>
          )}
          {isAuthenticated && !['Admin', 'SuperUser'].includes(user?.role) && (
            <Alert severity="warning" sx={{ flex: 1 }}>
              You need Admin or SuperUser role to access Pathasala Management.
              Current role: <strong>{user?.role}</strong>
            </Alert>
          )}
          {isAuthenticated && ['Admin', 'SuperUser'].includes(user?.role) && (
            <Button 
              variant="contained" 
              color="success"
              onClick={() => navigate('/pathasala')}
            >
              Go to Pathasala Management
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AuthDebug;
